import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldCheckIcon, BarChart3Icon, MegaphoneIcon, SettingsIcon } from 'lucide-react';
import { acceptAll, rejectAll, setConsent, getConsent, initConsent, OPEN_SETTINGS_EVENT } from '../lib/consent';
const NORDEN_COOKIE_IMG = 'https://cdn.magicpatterns.com/uploads/a7QwrfsRheFwXZaY2yk98u/Norden_die_cookie_eet_nom.png';
export const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);
  useEffect(() => {
    initConsent();
    const existing = getConsent();
    if (!existing) {
      const t = window.setTimeout(() => setVisible(true), 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);
  useEffect(() => {
    const open = () => {
      const existing = getConsent();
      if (existing) {
        setAnalytics(existing.categories.analytics);
        setMarketing(existing.categories.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);
  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    setShowDetails(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
    setShowDetails(false);
  };
  const handleSavePrefs = () => {
    setConsent({
      analytics,
      marketing
    });
    setVisible(false);
    setShowDetails(false);
  };
  return <AnimatePresence data-id="element-185">
      {visible && <>
          {/* Backdrop only when details open */}
          {showDetails && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-[9998]" onClick={() => setShowDetails(false)} data-id="element-186" />}

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 240,
        damping: 30
      }} role="dialog" aria-live="polite" aria-label="Cookie-instellingen" className="fixed bottom-0 left-0 right-0 z-[9999]" data-id="element-187">
            <div className="relative bg-white border-t-4 border-[#004AC7] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.25)]" data-id="element-188">
              {/* Norden — sits on top edge of the bar, feet planted on the border */}
              <div className="absolute left-4 md:left-12 lg:left-24 bottom-full translate-y-[40%] pointer-events-none select-none" data-id="element-189">
                <motion.img initial={{
              y: 20,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.15,
              duration: 0.4
            }} src={NORDEN_COOKIE_IMG} alt="Norden eet een cookie" className="w-32 h-32 md:w-44 md:h-44 lg:w-52 lg:h-52 object-contain object-bottom drop-shadow-xl" data-id="element-190" />
              </div>

              <div className="container mx-auto max-w-[1400px] px-4 md:px-8 py-5 md:py-6" data-id="element-191">
                <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8 lg:pl-[180px] xl:pl-[240px]" data-id="element-192">
                  {/* Copy */}
                  <div className="flex-1 min-w-0" data-id="element-193">
                    <h3 className="text-lg md:text-xl font-bold text-[#111827] mb-1.5" data-id="element-194">
                      Mag Norden een cookie?
                    </h3>
                    <p className="text-sm text-[#4B5563] leading-relaxed max-w-2xl" data-id="element-195">
                      Wij gebruiken cookies om de site te laten werken, om te
                      leren hoe bezoekers ons vinden en om onze content te
                      verbeteren. Jij bepaalt zelf wat we mogen bewaren.{' '}
                      <a href="/cookiebeleid" className="text-[#004AC7] font-semibold hover:underline" data-id="element-196">
                        Lees meer
                      </a>
                      .
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row lg:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-shrink-0" data-id="element-197">
                    <button onClick={() => setShowDetails(v => !v)} className="inline-flex items-center justify-center gap-2 text-[#1B2B44] font-medium py-2.5 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm" data-id="element-198">
                      <SettingsIcon className="w-4 h-4" data-id="element-199" />
                      Voorkeuren
                    </button>
                    <button onClick={handleRejectAll} className="text-[#1B2B44] font-medium py-2.5 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm" data-id="element-200">
                      Alleen noodzakelijk
                    </button>
                    <button onClick={handleAcceptAll} className="bg-[#004AC7] text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-[#003A9E] transition-colors shadow-sm" data-id="element-201">
                      Alles accepteren
                    </button>
                  </div>
                </div>

                {/* Expandable preferences panel */}
                <AnimatePresence initial={false} data-id="element-202">
                  {showDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-203">
                      <div className="pt-6 mt-6 border-t border-gray-100 lg:pl-[180px] xl:pl-[240px]" data-id="element-204">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-id="element-205">
                          <ConsentRow icon={<ShieldCheckIcon className="w-4 h-4" data-id="element-207" />} title="Noodzakelijk" description="Vereist om de site te laten werken, zoals het onthouden van je cookie-keuze." checked disabled onChange={() => {}} data-id="element-206" />
                          <ConsentRow icon={<BarChart3Icon className="w-4 h-4" data-id="element-209" />} title="Statistieken" description="Anonieme metingen via Google Analytics zodat we de site kunnen verbeteren." checked={analytics} onChange={setAnalytics} data-id="element-208" />
                          <ConsentRow icon={<MegaphoneIcon className="w-4 h-4" data-id="element-211" />} title="Marketing" description="Meta Pixel zodat we onze advertenties relevanter kunnen maken." checked={marketing} onChange={setMarketing} data-id="element-210" />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-end gap-2" data-id="element-212">
                          <button onClick={() => setShowDetails(false)} className="inline-flex items-center justify-center gap-2 text-[#6B7280] font-medium py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-213">
                            <XIcon className="w-4 h-4" data-id="element-214" />
                            Sluiten
                          </button>
                          <button onClick={handleSavePrefs} className="bg-[#004AC7] text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-[#003A9E] transition-colors text-sm" data-id="element-215">
                            Voorkeuren opslaan
                          </button>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
type ConsentRowProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
};
const ConsentRow: React.FC<ConsentRowProps> = ({
  icon,
  title,
  description,
  checked,
  disabled,
  onChange
}) => {
  return <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F7F8FA] border border-gray-100" data-id="element-216">
      <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center text-[#004AC7] flex-shrink-0" data-id="element-217">
        {icon}
      </div>
      <div className="flex-1 min-w-0" data-id="element-218">
        <div className="flex items-center justify-between gap-3 mb-1" data-id="element-219">
          <span className="font-semibold text-sm text-[#111827]" data-id="element-220">{title}</span>
          <button role="switch" aria-checked={checked} aria-label={title} disabled={disabled} onClick={() => !disabled && onChange(!checked)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-[#004AC7]' : 'bg-gray-300'} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-221">
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-222" />
          </button>
        </div>
        <p className="text-xs text-[#6B7280] leading-relaxed" data-id="element-223">{description}</p>
      </div>
    </div>;
};